import { ChartingService } from './../charting.service';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Chart, ILoadedEventArgs, SeriesModel } from '@syncfusion/ej2-angular-charts';
import { LogService } from 'src/app/services/log.service';
import { UtilsService } from 'src/app/services/utils.service';
import { ITapChartConfig } from '../tap-chart.component';
import * as lodash from 'lodash';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, delay, of, take, switchMap } from 'rxjs';
import { AgGridAngular } from '@ag-grid-community/angular';
import { ColDef, ColumnApi, GridApi, GridOptions, Module } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { DashboardWidgetService } from '@services/dashboard-widget.service';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/app.state';
import { selectWidgetQuery } from '@state/selectors/tank-profile.selectors';
import { CHART_COLORS } from 'src/app/shared/Constants/sharedConstants';

@Component({
  selector: 'app-tap-pop-out-compare-chart',
  templateUrl: './tap-pop-out-compare-chart.component.html',
  styleUrls: ['./tap-pop-out-compare-chart.component.css'],
})
export class TapPopOutCompareChartComponent implements OnInit {
  @ViewChild('agGrid', { static: false }) agGrid: AgGridAngular;
  @Input() chartLegend: TemplateRef<any>;
  @Input() chartTitle: string = 'chart title here';
  @Input() popOutLegendPosition: string;
  @Output() chartLoaded = new EventEmitter();

  public gridApi: GridApi;
  protected gridColumnApi: ColumnApi;
  columnDefs = this.getColumnDefs();
  defaultColDef: ColDef = {
    filter: true,
    sortable: true,
    resizable: true,
    suppressMovable: true,
    lockPinned: true,
    editable: false,
    suppressColumnsToolPanel: true,
  };
  gridOptions: GridOptions = {
    rowSelection: 'multiple',
    suppressRowClickSelection: false,
  };
  modules: Module[] = [MenuModule, ClientSideRowModelModule, SetFilterModule, ExcelExportModule];
  rowData$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  legendOrientation: string;
  isLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  onAxisRenderedCallback: (...args: any[]) => void = () => {};
  onPointRenderCallback: (...args: any[]) => void = () => {};
  onTooltipRenderCallback: (...args: any[]) => void = () => {};
  onLegendClickCallback: (...args: any[]) => void = () => {};
  private readonly dottedDashArray = '1, 2';
  private readonly chartTheme = 'Material';
  private chartApi: Chart;
  private backgroundColor = '#ffffff00';
  private seriesFillColor = this.utilsService.getSiteWideThemeBackgroundColorHEXValue();
  gridData: any;
  chartVisible = true;
  isGroupView = true;

  showControls$ = new BehaviorSubject<boolean>(true);
  chartDefaultLegendDisplay = false;

  fontFamily = this.utilsService.getSiteWideThemeFontFamily();

  labelStyle = {
    fontFamily: this.fontFamily,
    fontWeight: 'Bold',
  };

  _chartConfig: ITapChartConfig | null = null;
  private _groupViewChartConfig: ITapChartConfig | null = null;
  private _groupViewChartData: any[] = [];

  get chartConfig() {
    return this._chartConfig;
  }

  @Input()
  set chartConfig(value: ITapChartConfig) {
    this.chartTitle = value.chartTitle;

    // adjust chart height and width
    let chartHeight = '100%';
    let chartWidth = '';
    if (value?.chartType !== 'Pie') {
      chartHeight = value?.chartHeight ?? '50%';
      //chartHeight = "60%" ;
      chartWidth = '100%';
    }

    let displayLegendInChart = value?.displayLegend ?? false;
    this.chartDefaultLegendDisplay = displayLegendInChart;
    let newLegendSettings = lodash.cloneDeep(value.legendSettings);
    newLegendSettings.visible = true; //displayLegendInChart;

    // Create a new config object each time so Angular input binding
    // detects chartConfig changes in the child tap-chart component.
    const mergedConfig: ITapChartConfig = lodash.merge({}, this._chartConfig, value, {
      chartTitle: null,
      chartHeight: chartHeight,
      chartWidth: chartWidth,
      legendSettings: newLegendSettings,
    } as any);

    // Replace series array outright to avoid stale merge-by-index behavior.
    if (value?.chartSeries) {
      mergedConfig.chartSeries = value.chartSeries;
    }

    this._chartConfig = mergedConfig;
    if (this._chartConfig.chartTheme.toLowerCase().includes('dark')) {
      this._chartConfig.backgroundColor = this.utilsService.getSiteWideThemeDarkColorHEXValue();
      this._chartConfig.chartArea.background =
        this.utilsService.getSiteWideThemeDarkColorHEXValue();
    }

    // IMPORTANT: Only refresh the chart if BOTH conditions are true:
    // 1. chartApi exists (this modal's chart is initialized)
    // 2. _chartData exists (chart has data loaded)
    // This prevents accidental updates to parent chart instances during initialization
    if (this.chartApi && this._chartData?.length) {
      this.refreshChartInternal();
    }
  }

  _chartData = [];
  get chartData() {
    return this._chartData;
  }
  @Input() set chartData(value) {
    this._chartData = value;

    if (!this.chartApi || !value?.length) return;
    this.refreshChartInternal();
  }

  subheaderVM?: {
    userName: string;
    profileName: string;
    orgName?: string;
    childOrgName?: string;
    regionName?: string;
    dateRange?: string;
  } = null;

  private commonSeriesConfig: SeriesModel = {
    animation: {
      enable: false, //disable animation on series selection
      duration: 50, //milliseconds of animation
      delay: 0,
    },
  };

  /* String Resource | Value Binding */
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      chartConfig: ITapChartConfig;
      chartData: any[];
      chartLegend: TemplateRef<any>;
      onAxisLabelRenderCallback: ($event: any) => void;
      onPointRenderCallback: ($event: any) => void;
      onTooltipRenderCallback: ($event: any) => void;
      popOutLegendPosition: string;
      onLegendClickCallback: ($event: any) => void;
    },
    private changeDetectorRef: ChangeDetectorRef,
    public dialogRef: MatDialogRef<TapPopOutCompareChartComponent>,
    public utilsService: UtilsService,
    protected logger: LogService,
    private matDialog: MatDialog,
    private chartingService: ChartingService,
    private _util: UtilsService,
    private store: Store<AppState>,
    private dashboardWidgetService: DashboardWidgetService,
  ) {
    if (!this.data) {
      return;
    }

    // Save originals so the Group View button can restore the comparison chart
    this._groupViewChartConfig = lodash.cloneDeep(this.data.chartConfig);
    this._groupViewChartData = this.data.chartData ? lodash.cloneDeep(this.data.chartData) : [];

    // Use deep clones to prevent mutations in the modal from affecting the parent dashboard chart
    this.chartData = this.data.chartData ? lodash.cloneDeep(this.data.chartData) : [];
    this.chartConfig = lodash.cloneDeep(this.data.chartConfig);

    this.chartLegend = this.data.chartLegend;
    this.onAxisRenderedCallback = this.data.onAxisLabelRenderCallback;
    this.onPointRenderCallback = this.data.onPointRenderCallback;
    this.onTooltipRenderCallback = this.data.onTooltipRenderCallback;
    this.popOutLegendPosition = this.data.popOutLegendPosition;
    this.onLegendClickCallback = this.data.onLegendClickCallback;
  }

  async ngOnInit(): Promise<void> {
    this.subheaderVM = await this.chartingService.GetPopUpChartSubheaderModel();
    this.isLoaded$.next(true);
    // set up legend orientation
    if (this.popOutLegendPosition === 'bottom') this.legendOrientation = 'column';
    else this.legendOrientation = 'row';
    // removed automatic window resize dispatch; rely on refresh helper instead
    this.fetchDeliveryEfficiencyDataForTanks();
  }

  //Setup additional chart features.
  public chartLoad(args: ILoadedEventArgs): void {
    //chartLoad event can happen more than once (refreshes or updates to chart config trigger this)
    if (this.chartApi) return;

    this.chartApi = args.chart;

    //setup tooltip options. This is left to typescript code since we have two tooltip directives.
    // one is for Syncfusion charting to define congiguration and the other is to setup a tooltip pop-up material themed
    //component
    this.setupChartTooltip();

    if (this._chartData?.length) {
      this.refreshChartInternal();
    }
  }

  public pieChartLoad(args: ILoadedEventArgs): void {
    //todo:
    this.chartApi = args.chart;
    this.setupChartTooltip();

    if (this._chartData?.length) {
      this.refreshChartInternal();
    }
  }

  public onChartLoaded(args: any): void {
    this.chartLoaded.emit({ context: this, args });
  }

  public onChartClick(): void {
    //do nothing, stub code.
  }

  // public onLegendClick($event) {
  //   this.onLegendClickCallback($event);
  //   this.logger.trackEvent(this._chartConfig.chartTitle, "legendClick");

  public refreshChart(): void {
    this.refreshChartInternal();
  }

  private refreshChartInternal(): void {
    if (!this.chartApi) {
      return;
    }

    try {
      this.chartApi.refreshBound();
    } catch (error) {
      // no-op
    }

    try {
      this.chartApi.refresh();
    } catch (error) {
      // no-op
    }

    this.changeDetectorRef.detectChanges();
  }

  private setupChartTooltip() {
    this.chartApi.tooltip.enable = this.chartConfig.tooltip ?? true;
    this.chartApi.tooltip.textStyle = this.labelStyle;
  }

  onAxisLabelRender($event) {
    this.onAxisRenderedCallback($event);
  }

  onPointRender($event) {
    this.onPointRenderCallback($event);
  }

  onTooltipRender($event) {
    this.onTooltipRenderCallback($event);
  }

  onCloseClick() {
    this.close();
  }

  onDownloadImageClick() {
    this.extractImageAndDownloadToClient();
  }

  close = () => {
    this.dialogRef.close();
  };

  private getColumnDefs(): ColDef[] {
    return [
      {
        field: 'tankId',
        hide: true,
      },
      {
        headerName: 'Tank Name', //this.translate.instant('User.UserTankProfilesGridColumn.ProfileName',),
        field: 'tankName',
        flex: 30,
        minWidth: 120,
        filter: true,
      },
      {
        headerName: 'RTU Number', //this.translate.instant('User.UserTankProfilesGridColumn.ProfileName',),
        field: 'rtuNumber',
        flex: 30,
        minWidth: 120,
        filter: true,
      },
      {
        headerName: 'Prod Desc', //this.translate.instant('User.UserTankProfilesGridColumn.ProfileName',),
        field: 'prodDesc',
        flex: 30,
        minWidth: 120,
        filter: true,
      },
    ];
  }

  extractImageAndDownloadToClient() {
    //hide the print button control or else it will be printed
    const element: HTMLElement = document.querySelector('.chart-print-area');
    this.showControls$.next(false);

    setTimeout(async () => {
      const html2canvas = (await import('html2canvas')).default;
      html2canvas(element)
        .then(function (canvas) {
          // Convert the canvas to blob
          canvas.toBlob(function (blob) {
            // To download directly on browser default 'downloads' location
            let link = document.createElement('a');
            link.download = 'image.png';
            link.href = URL.createObjectURL(blob);
            link.click();
            // To save manually somewhere in file explorer
            //window.saveAs(blob, 'image.png');
          }, 'image/png');
        })
        .finally(() => {
          this.showControls$.next(true);
        });
    }, 250);
  }

  fetchDeliveryEfficiencyDataForTanks() {
    this.store
      .select(selectWidgetQuery)
      .pipe(
        take(1),
        switchMap(query => {
          return this.dashboardWidgetService.getHistoricalDeliveryEfficienciesForTanks(query);
        }),
      )
      .subscribe(data => {
        const normalizedMonths = data.months.map(m => m.toLowerCase());
        const normalizedRows = (data.gridRows || []).map(row => ({
          ...row,
          monthlyData: Object.fromEntries(
            Object.entries(row.monthlyData || {}).map(([k, v]) => [k.toLowerCase(), v]),
          ),
        }));
        this.gridData = normalizedRows;

        this.buildGridColumns(normalizedMonths);

        //this.buildGridColumns(data.months);

        this.rowData$.next(normalizedRows);

        if (this.gridApi) {
          this.gridApi.setColumnDefs(this.columnDefs);
        }
      });
  }

  onGridReady(params) {
    this.gridApi = params.api;
  }

  buildGridColumns(months: string[]) {
    this.columnDefs = [
      {
        headerName: 'TankName',
        field: 'tankName',
        // Always render a checkbox for each row and provide a header checkbox for select-all
        checkboxSelection: (params) => true,
        headerCheckboxSelection: true,
        //width: 250,
        minWidth: 230,
        //maxWidth: 270,
        resizable: true,
        //valueGetter: params => `${params.data.tankName} - ${params.data.tankId}`
      },

      {
        headerName: 'RTU Number',
        field: 'rtuNumber',
        width: 200,
        minWidth: 180,
        maxWidth: 220,
        resizable: true,
      },
      {
        headerName: 'Prod Desc',
        field: 'prodDesc',
        minWidth: 200,
        resizable: true,
      },
      ...months.map(month => ({
        headerName: month.replace(/^\w/, c => c.toUpperCase()), // display nicely
        width: 110,
        valueGetter: params => params.data?.monthlyData?.[month] ?? null,
        valueFormatter: params =>
          params.value != null ? (params.value * 100).toFixed(2) + '%' : '',
      })),
    ];

    if (this.gridApi) {
      this.gridApi.setColumnDefs(this.columnDefs);
    }
  }
  public onRowSelected(): void {
    const selectedRows = this.gridApi.getSelectedRows();

    if (!selectedRows.length) {
      this.isGroupView = true;
      return;
    }

    this.isGroupView = false;

    const series: SeriesModel[] = selectedRows.map((row, i) => {
      const color = CHART_COLORS[i % CHART_COLORS.length];

      const dataPoints = Object.entries(row.monthlyData || {})
        .filter(([, v]) => v != null)
        .map(([month, value]: [string, any]) => ({
          date: this.parseMonthYear(month.toLowerCase()),
          averageDeliveryEfficiency: value,
        }))
        .sort((a, b) => a.date.getTime() - b.date.getTime());

      return {
        dataSource: dataPoints,
        xName: 'date',
        yName: 'averageDeliveryEfficiency',
        name: `${row.tankName} (${row.rtuNumber})`,
        type: 'Line',
        width: 2,
        fill: color,
        marker: { visible: true, shape: 'Circle', width: 8, height: 8, fill: color },
      };
    });

    this.applyChartConfigForSeries(series);
  }
  private buildAggregatedChart(rows: any[], product: string) {
    const monthMap: Record<string, { sum: number; count: number }> = {};

    rows.forEach(row => {
      Object.entries(row.monthlyData || {}).forEach(([month, value]: any) => {
        if (typeof value !== 'number') return;

        const key = month.toLowerCase();

        if (!monthMap[key]) {
          monthMap[key] = { sum: 0, count: 0 };
        }

        monthMap[key].sum += value;
        monthMap[key].count += 1;
      });
    });

    // One point per month
    const dataPoints = Object.keys(monthMap).map(month => ({
      date: this.parseMonthYear(month),
      averageDeliveryEfficiency: monthMap[month].sum / monthMap[month].count,
    }));

    // Sort by date
    dataPoints.sort((a, b) => a.date.getTime() - b.date.getTime());

    const series: SeriesModel[] = [
      {
        dataSource: dataPoints,
        xName: 'date',
        yName: 'averageDeliveryEfficiency',
        name: product,
        type: 'Line',
        width: 2,
        marker: { visible: true },
      },
    ];

    this.applyChartConfigForSeries(series);
  }

  private buildMultiProductChart(rows: any[]) {
    const products = [...new Set(rows.map(r => r.prodDesc))];

    const series: SeriesModel[] = products.map((product, i) => {
      const productKey = (product || '').trim().toUpperCase();
      const productRows = rows.filter(r => (r.prodDesc || '').trim().toUpperCase() === productKey);
      // Step 1: aggregate per month
      const monthMap: Record<string, { sum: number; count: number }> = {};

      productRows.forEach(row => {
        Object.entries(row.monthlyData || {}).forEach(([month, value]: any) => {
          if (typeof value !== 'number') return;

          const key = month.toLowerCase();

          if (!monthMap[key]) {
            monthMap[key] = { sum: 0, count: 0 };
          }

          monthMap[key].sum += value;
          monthMap[key].count += 1;
        });
      });

      // Step 2: build one point per month
      const dataPoints = Object.keys(monthMap).map(month => ({
        date: this.parseMonthYear(month),
        averageDeliveryEfficiency: monthMap[month].count
          ? monthMap[month].sum / monthMap[month].count
          : null,
      }));

      // Step 3: sort by date
      dataPoints.sort((a, b) => a.date.getTime() - b.date.getTime());

      return {
        dataSource: dataPoints,
        xName: 'date',
        yName: 'averageDeliveryEfficiency',
        name: product,
        type: 'Line',
        width: 2,
        fill: CHART_COLORS[i % CHART_COLORS.length],
        marker: { visible: true },
      };
    });

    this.applyChartConfigForSeries(series);
  }

  private applyChartConfig(series: SeriesModel[], chartData: any[]) {
    // Extract numeric values safely
    const values = chartData.flatMap(d =>
      Object.values(d).filter((v): v is number => typeof v === 'number'),
    );

    const padding = 0.02;

    const axisMin = values.length ? Math.max(0, Math.min(...values) - padding) : 0;

    const axisMax = values.length ? Math.max(...values) + padding : 1;

    // Clear chart data (forces reset in shared component)
    this.chartData = [];

    this.chartConfig = {
      ...this.chartConfig,

      chartSeries: series.map(s => ({ ...s })),

      disableLegendToggle: true,

      primaryXAxis: {
        valueType: 'DateTime',
        labelFormat: 'MMM yyyy',
      },

      primaryYAxis: {
        ...this.chartConfig.primaryYAxis,
        minimum: axisMin,
        maximum: axisMax,
      },
    };

    // Delay setting data (sync with internal destroy/recreate)
    setTimeout(() => {
      this.chartData = [...chartData]; // new reference
    }, 50);
  }

  private parseMonthYear(key: string): Date {
    const [monthStr, yearStr] = key.toLowerCase().split(' ');

    const monthMap = {
      jan: 0,
      feb: 1,
      mar: 2,
      apr: 3,
      may: 4,
      jun: 5,
      jul: 6,
      aug: 7,
      sep: 8,
      oct: 9,
      nov: 10,
      dec: 11,
    };

    return new Date(Number(yearStr), monthMap[monthStr], 1);
  }
  private applyChartConfigForSeries(series: SeriesModel[]) {
    this.logger.log('=== APPLY CHART CONFIG ===');
    this.logger.log(`Incoming series count: ${series.length}`);

    const allData: any[] = [];

    series.forEach((s, idx) => {
      const dataPoints = ((s.dataSource as any[]) || []);
      this.logger.log(`  Series ${idx} (${s.name}): ${dataPoints.length} points`);
      dataPoints.forEach(d => {
        allData.push(d);
      });
    });

    this.logger.log(`Total aggregated data points: ${allData.length}`);

    const values = allData
      .map(d => d.averageDeliveryEfficiency)
      .filter((v): v is number => typeof v === 'number');

    // Keep strip lines, but size the visible axis to best-fit selected data.
    const stripLines: any[] = this._chartConfig?.primaryYAxis?.stripLines ?? [];
    const targetValue: number | null = stripLines.length > 0 ? (stripLines[0].start ?? null) : null;

    const dataMin = values.length ? Math.min(...values) : 0;
    const dataMax = values.length ? Math.max(...values) : 1;
    const spread = Math.max(0, dataMax - dataMin);

    // Dynamic padding gives a tighter fit for narrow ranges and enough breathing room for wider ranges.
    const dynamicPadding = spread > 0
      ? Math.min(0.08, Math.max(0.008, spread * 0.12))
      : Math.max(0.01, Math.abs(dataMax) * 0.1);

    let axisMin = Math.max(0, dataMin - dynamicPadding);
    let axisMax = dataMax + dynamicPadding;

    // Guard against degenerate ranges (all points equal or a single point).
    if (axisMax <= axisMin) {
      axisMax = axisMin + Math.max(0.02, dynamicPadding);
    }

    // Note: do NOT use JSON.stringify here - it turns Date objects into strings,
    // which breaks the Syncfusion DateTime axis. Pass the config directly so
    // lodash.merge (called inside the setter) can cloneDeep with real Dates.
    const newConfig: any = {
      chartType: this.chartConfig?.chartType ?? 'Linear',

      chartSeries: series,

      primaryXAxis: {
        valueType: 'DateTime',
        labelFormat: 'MMM yyyy',
      },

      primaryYAxis: {
        minimum: axisMin,
        maximum: axisMax,
        ...(stripLines.length > 0 ? { stripLines: [...stripLines] } : {}),
      },

      legendSettings: {
        visible: true,
      },

      chartTheme: this.chartConfig?.chartTheme ?? 'Material',
      backgroundColor: this.chartConfig?.backgroundColor ?? '#ffffff00',
    };

    // Clear stale series before lodash.merge runs in the setter,
    // otherwise merge-by-index leaves old series in place.
    if (this._chartConfig) {
      this._chartConfig.chartSeries = [];
    }
    // Use object spread to force immutable config update, preventing
    // lodash.merge from partially preserving old series
    this.chartConfig = {
      ...newConfig,
      chartSeries: newConfig.chartSeries
    };
    this.chartData = allData.map(d => ({ ...d }));

    if (this.chartApi?.primaryYAxis) {
      this.chartApi.primaryYAxis = {
        ...this.chartApi.primaryYAxis,
        minimum: axisMin,
        maximum: axisMax,
      };
      this.refreshChartInternal();
    }
  }

  public resetToGroupView(): void {
    if (!this._groupViewChartConfig) {
      return;
    }

    this.isGroupView = true;
    this.gridApi?.deselectAll();

    if (this._chartConfig) {
      this._chartConfig.chartSeries = [];
    }

    this.chartConfig = lodash.cloneDeep(this._groupViewChartConfig);
    this.chartData = [...this._groupViewChartData];
  }
}
